/**
 * VIKKI — SALAR website guide (Phase 1, local mode)
 *
 * Phase 1 is strictly deterministic. No external AI API. No private SALAR
 * content. Routes a visitor to public pages, drafts a mailto, or pre-fills
 * the contact form. Unknown / private questions fall back to a safe
 * "save to internal review" record stored on window.__vikkiInquiries so a
 * SALAR reviewer can pick it up offline. Visitors never see internal flags.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MessageSquare, Send, X, ChevronRight, Mail, FileText, ShieldCheck, Sparkles } from 'lucide-react';

import { VIKKI_KNOWLEDGE, SALAR_PUBLIC_FACTS } from '../data/vikkiKnowledge';
import { searchPages, inferTopic, tokenize, TOPIC_TO_PRIMARY_PATH } from '../utils/vikkiSearch';
import { buildEmailDraft, buildContactPrefillUrl, buildInquiryRecord } from '../utils/vikkiEmailDraft';

/* -------------------------------------------------------------------------
 * Constants — public-safe canned responses + quick prompts
 * --------------------------------------------------------------------- */

const QUICK_PROMPTS = [
  { label: 'What does SALAR do?',  query: 'What does SALAR do?' },
  { label: 'Open the demo',        query: 'Open the demo' },
  { label: 'Patents',              query: 'Tell me about patents' },
  { label: 'AI Fabric',            query: 'AI Fabric' },
  { label: 'Compliance',           query: 'Section 889 / Compliance' },
  { label: 'Contact',              query: 'Contact SALAR' },
];

const GREETING_MESSAGE = {
  role: 'assistant',
  kind: 'greeting',
  text:
    "Hi, I'm VIKKI — the SALAR Technologies website guide. " +
    'I can route you to the right page, draft an email, or pre-fill the contact form. ' +
    'Try a quick prompt below or ask anything about SALAR.',
};

/* -------------------------------------------------------------------------
 * Pure response builder — easy to unit-test
 * --------------------------------------------------------------------- */

function findPageByPath(path) {
  return VIKKI_KNOWLEDGE.find((p) => p.path === path) || null;
}

function buildAssistantResponse({ query, pageOfOrigin }) {
  const trimmed = String(query || '').trim();
  if (!trimmed) {
    return {
      role: 'assistant',
      kind: 'empty',
      text: 'Type a question and I will point you to the right SALAR page or help draft an email.',
    };
  }

  const topic = inferTopic(trimmed);
  const tokens = tokenize(trimmed);
  const matches = searchPages(trimmed, 3);

  // --- Identity guard: VIKKI is the website guide, not Vikrant ---
  if (topic === 'identity') {
    return {
      role: 'assistant',
      kind: 'identity',
      text:
        'I am VIKKI, the SALAR Technologies website guide — not Vikrant Salar and not a generative AI. ' +
        "I'm a deterministic guide that helps visitors find the right public page or draft a message. " +
        'For a personal reply from the team, I can pre-fill the contact form or draft an email.',
      actions: [
        { kind: 'route',     path: '/about',   label: 'About the founder' },
        { kind: 'prefill',   topic: 'contact', label: 'Pre-fill contact form', question: trimmed },
        { kind: 'mailto',    topic: 'contact', label: 'Draft email',           question: trimmed },
      ],
    };
  }

  // --- Private / internal-strategy guard: never expose, always escalate ---
  if (topic === 'private') {
    const inquiry = buildInquiryRecord({
      visitorQuestion: trimmed,
      pageOfOrigin,
      pagesSearched: matches.map((m) => m.page.path),
      matchedKeywords: tokens,
      confidence: 'Low',
      suggestedReply: 'Public-safe routing only; defer to SALAR review.',
      recommendedPage: '/contact',
      topic: 'private',
    });
    logInquiry(inquiry);
    return {
      role: 'assistant',
      kind: 'safe_escalation',
      text:
        'That sounds like an internal or non-public topic. I only share information that is already published on this site. ' +
        'I have saved your question for SALAR review and can also help you reach the team directly.',
      inquiryId: inquiry.id,
      actions: [
        { kind: 'prefill', topic: 'private', label: 'Pre-fill contact form', question: trimmed },
        { kind: 'mailto',  topic: 'private', label: 'Draft email',           question: trimmed },
      ],
    };
  }

  // --- High-confidence: top-page match plus offers ---
  if (matches.length > 0 && matches[0].score >= 6) {
    const primary = matches[0].page;
    const related = matches.slice(1, 3).map((m) => m.page);

    return {
      role: 'assistant',
      kind: 'route',
      text: `Here is the most relevant page on salartechnologies.com — ${primary.title}. ${primary.summary}`,
      primary,
      related,
      actions: [
        { kind: 'route',   path: primary.path,                                 label: `Open ${primary.title}` },
        { kind: 'prefill', topic,                                              label: 'Pre-fill contact form', question: trimmed },
        { kind: 'mailto',  topic,                                              label: 'Draft email',           question: trimmed },
      ],
    };
  }

  // --- Topic-only fallback (low match score, but topic inferred) ---
  const topicPath = TOPIC_TO_PRIMARY_PATH[topic];
  if (topicPath) {
    const primary = findPageByPath(topicPath);
    if (primary) {
      return {
        role: 'assistant',
        kind: 'route',
        text: `Closest match on salartechnologies.com — ${primary.title}. ${primary.summary}`,
        primary,
        related: matches.slice(0, 2).map((m) => m.page),
        actions: [
          { kind: 'route',   path: primary.path, label: `Open ${primary.title}` },
          { kind: 'prefill', topic,              label: 'Pre-fill contact form', question: trimmed },
          { kind: 'mailto',  topic,              label: 'Draft email',           question: trimmed },
        ],
      };
    }
  }

  // --- Unknown — safe escalation to email + internal-review queue ---
  const inquiry = buildInquiryRecord({
    visitorQuestion: trimmed,
    pageOfOrigin,
    pagesSearched: matches.map((m) => m.page.path),
    matchedKeywords: tokens,
    confidence: 'Low',
    suggestedReply: 'No direct public-page match; offer email + contact prefill.',
    recommendedPage: '/contact',
    topic: 'general',
  });
  logInquiry(inquiry);

  return {
    role: 'assistant',
    kind: 'fallback',
    text:
      "I could not find an exact page for that on the public site, but I can still help. " +
      'You can browse our capabilities, or I can pre-fill the contact form or draft an email to the SALAR team.',
    inquiryId: inquiry.id,
    actions: [
      { kind: 'route',   path: '/capabilities', label: 'Open Capabilities' },
      { kind: 'prefill', topic: 'general',      label: 'Pre-fill contact form', question: trimmed },
      { kind: 'mailto',  topic: 'general',      label: 'Draft email',           question: trimmed },
    ],
  };
}

/* -------------------------------------------------------------------------
 * Internal-review queue (mock, in-memory) — Phase 2 will replace with API
 * --------------------------------------------------------------------- */

function logInquiry(record) {
  try {
    if (typeof window === 'undefined') return;
    if (!Array.isArray(window.__vikkiInquiries)) {
      window.__vikkiInquiries = [];
    }
    window.__vikkiInquiries.push(record);
  } catch (_) {
    /* no-op */
  }
}

/* -------------------------------------------------------------------------
 * Sub-components
 * --------------------------------------------------------------------- */

const MessageBubble = ({ message, onAction }) => {
  const isUser = message.role === 'user';

  return (
    <div
      className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-3`}
      data-testid={isUser ? 'vikki-msg-user' : 'vikki-msg-assistant'}
    >
      <div
        className={[
          'max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed',
          isUser
            ? 'bg-cyan-600/90 text-white rounded-br-sm'
            : 'bg-[#0F1A2B] border border-blue-500/30 text-blue-50 rounded-bl-sm',
        ].join(' ')}
      >
        <p className="whitespace-pre-wrap">{message.text}</p>

        {/* Related links (route kind only) */}
        {message.related && message.related.length > 0 && (
          <ul className="mt-2 pt-2 border-t border-blue-500/20 space-y-1">
            {message.related.map((r) => (
              <li key={r.path}>
                <button
                  type="button"
                  onClick={() => onAction({ kind: 'route', path: r.path, label: r.title })}
                  className="inline-flex items-center text-xs text-cyan-300 hover:text-cyan-200 transition-colors"
                  data-testid={`vikki-related-${r.path.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
                >
                  <ChevronRight size={12} className="mr-1" />
                  Related: {r.title}
                </button>
              </li>
            ))}
          </ul>
        )}

        {/* Action buttons */}
        {message.actions && message.actions.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {message.actions.map((a, i) => {
              const Icon = a.kind === 'mailto' ? Mail : a.kind === 'prefill' ? FileText : ChevronRight;
              const testKey = `${a.kind}-${(a.path || a.topic || a.label).toString().replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`;
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => onAction(a)}
                  className="inline-flex items-center gap-1.5 rounded-full border border-cyan-400/40 bg-cyan-500/10 px-3 py-1 text-xs font-medium text-cyan-100 hover:bg-cyan-500/20 hover:border-cyan-400/70 transition-colors"
                  data-testid={`vikki-action-${testKey}`}
                >
                  <Icon size={12} />
                  {a.label}
                </button>
              );
            })}
          </div>
        )}

        {/* Internal-review confirmation (visitor-friendly, no flags shown) */}
        {message.inquiryId && (
          <p className="mt-2 pt-2 border-t border-blue-500/20 text-[11px] text-blue-200/70 inline-flex items-center gap-1">
            <ShieldCheck size={11} />
            Saved for SALAR review (ref {message.inquiryId.slice(-6)})
          </p>
        )}
      </div>
    </div>
  );
};

/* -------------------------------------------------------------------------
 * Main component
 * --------------------------------------------------------------------- */

const VikkiAssistant = () => {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([GREETING_MESSAGE]);
  const location = useLocation();
  const navigate = useNavigate();

  const panelRef = useRef(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const launcherRef = useRef(null);

  /* ----- Scroll to bottom on new messages ----- */
  useEffect(() => {
    if (!open) return;
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, open]);

  /* ----- Focus input on open ----- */
  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  /* ----- Esc closes panel + return focus to launcher ----- */
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        if (launcherRef.current) launcherRef.current.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  /* ----- Submit a query ----- */
  const submitQuery = useCallback(
    (rawQuery) => {
      const text = String(rawQuery || '').trim();
      if (!text) return;
      const userMsg = { role: 'user', kind: 'text', text };
      const reply   = buildAssistantResponse({ query: text, pageOfOrigin: location.pathname });
      setMessages((prev) => [...prev, userMsg, reply]);
      setInput('');
    },
    [location.pathname]
  );

  /* ----- Handle action button clicks ----- */
  const handleAction = useCallback(
    (action) => {
      if (!action) return;
      switch (action.kind) {
        case 'route': {
          if (action.path) {
            navigate(action.path);
            setOpen(false);
          }
          break;
        }
        case 'prefill': {
          const url = buildContactPrefillUrl({ topic: action.topic, question: action.question || '' });
          navigate(url);
          setOpen(false);
          break;
        }
        case 'mailto': {
          const draft = buildEmailDraft({ topic: action.topic, question: action.question || '' });
          // Open in user's default mail client — visitor still clicks Send.
          window.location.href = draft.mailto;
          break;
        }
        default:
          break;
      }
    },
    [navigate]
  );

  const handleSend = (e) => {
    e.preventDefault();
    submitQuery(input);
  };

  const handleQuickPrompt = (q) => submitQuery(q);

  /* ----- Track mobile viewport for panel sizing (reactive to resize) ----- */
  const [isMobileViewport, setIsMobileViewport] = useState(
    typeof window !== 'undefined' && window.matchMedia('(max-width: 640px)').matches
  );
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(max-width: 640px)');
    const handler = (e) => setIsMobileViewport(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', handler);
    else mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', handler);
      else mq.removeListener(handler);
    };
  }, []);

  /* ----- Indexed page count for footer note ----- */
  const indexedCount = useMemo(() => VIKKI_KNOWLEDGE.length, []);

  // Robust fixed-position styles applied inline so production CSS purge
  // can never strip them, and so safe-area + dynamic-viewport behave correctly.
  const launcherStyle = {
    position: 'fixed',
    right:  'max(16px, env(safe-area-inset-right))',
    bottom: 'calc(20px + env(safe-area-inset-bottom))',
    zIndex: 99999,
  };

  const panelStyleDesktop = {
    position: 'fixed',
    right:  'max(20px, env(safe-area-inset-right))',
    bottom: 'calc(88px + env(safe-area-inset-bottom))',
    width:  'min(420px, calc(100vw - 32px))',
    maxHeight: 'min(720px, calc(100dvh - 120px))',
    zIndex: 99999,
  };

  const panelStyleMobile = {
    position: 'fixed',
    left:   'max(12px, env(safe-area-inset-left))',
    right:  'max(12px, env(safe-area-inset-right))',
    bottom: 'calc(76px + env(safe-area-inset-bottom))',
    width:  'auto',
    maxHeight: 'calc(100dvh - 110px)',
    zIndex: 99999,
  };

  // Pick desktop vs mobile sizing via media query (reactive — see useEffect above).
  const panelStyle = isMobileViewport ? panelStyleMobile : panelStyleDesktop;

  return (
    <>
      {/* Launcher */}
      {!open && (
        <button
          ref={launcherRef}
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Open VIKKI website guide"
          data-testid="vikki-launcher"
          style={launcherStyle}
          className={[
            'shadow-[0_8px_30px_rgba(0,180,220,0.25)]',
            // Mobile: compact circular
            'h-12 w-12 rounded-full',
            // Desktop (sm+): pill with label
            'sm:h-auto sm:w-auto sm:rounded-full sm:px-5 sm:py-3',
            'flex items-center justify-center gap-2',
            'bg-[#0a1628] border border-cyan-400/40 text-cyan-100',
            'hover:bg-[#102341] hover:border-cyan-300/70',
            'focus:outline-none focus:ring-2 focus:ring-cyan-400/60',
            'transition-colors duration-200',
          ].join(' ')}
        >
          <MessageSquare size={20} className="text-cyan-300" />
          <span className="hidden sm:inline text-sm font-semibold tracking-wide whitespace-nowrap">Ask VIKKI</span>
        </button>
      )}

      {/* Mobile backdrop when open */}
      {open && (
        <div
          aria-hidden="true"
          onClick={() => setOpen(false)}
          style={{ position: 'fixed', inset: 0, zIndex: 99998 }}
          className="bg-black/40 backdrop-blur-[2px] sm:hidden"
          data-testid="vikki-backdrop"
        />
      )}

      {/* Chat panel */}
      {open && (
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="false"
          aria-label="VIKKI website guide"
          data-testid="vikki-panel"
          style={panelStyle}
          className="flex flex-col overflow-hidden rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.45)] bg-[#0a1628] border border-blue-500/30"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-blue-500/30 bg-[#0F1A2B]">
            <div className="flex items-center gap-2 min-w-0">
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-cyan-500/15 border border-cyan-400/40 text-cyan-300">
                <Sparkles size={16} />
              </span>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-white leading-tight">VIKKI</p>
                <p className="text-[11px] text-blue-200/70 truncate">SALAR website guide · local mode</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close VIKKI"
              data-testid="vikki-close"
              className="rounded-full p-1.5 text-blue-200 hover:text-white hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-cyan-400/60 transition-colors"
            >
              <X size={18} />
            </button>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto px-4 py-4 bg-gradient-to-b from-[#0a1628] to-[#08111f]"
            aria-live="polite"
            data-testid="vikki-messages"
          >
            {messages.map((m, idx) => (
              <MessageBubble key={idx} message={m} onAction={handleAction} />
            ))}
          </div>

          {/* Quick prompts (only show when conversation is just the greeting) */}
          {messages.length === 1 && (
            <div className="px-4 pb-2 -mt-2">
              <p className="text-[11px] uppercase tracking-wider text-blue-300/70 mb-2">Try a quick prompt</p>
              <div className="flex flex-wrap gap-2">
                {QUICK_PROMPTS.map((p) => (
                  <button
                    key={p.label}
                    type="button"
                    onClick={() => handleQuickPrompt(p.query)}
                    className="rounded-full border border-blue-500/40 bg-blue-500/10 px-3 py-1 text-xs text-blue-100 hover:bg-blue-500/20 hover:border-blue-400/70 transition-colors"
                    data-testid={`vikki-quick-${p.label.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Composer */}
          <form
            onSubmit={handleSend}
            className="flex items-center gap-2 px-3 py-3 border-t border-blue-500/30 bg-[#0F1A2B]"
            data-testid="vikki-composer"
          >
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about SALAR…"
              aria-label="Ask VIKKI a question"
              className="flex-1 rounded-full bg-[#0a1628] border border-blue-500/30 px-4 py-2 text-sm text-blue-50 placeholder-blue-300/50 focus:outline-none focus:border-cyan-400/70 focus:ring-2 focus:ring-cyan-400/30"
              data-testid="vikki-input"
              maxLength={500}
            />
            <button
              type="submit"
              disabled={!input.trim()}
              aria-label="Send"
              data-testid="vikki-send"
              className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-cyan-500 text-[#0a1628] hover:bg-cyan-400 disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-cyan-400/60 transition-colors"
            >
              <Send size={16} />
            </button>
          </form>

          {/* Footer note */}
          <p className="px-4 pb-3 text-[10px] text-blue-300/50 text-center">
            VIKKI is a deterministic guide across {indexedCount} public pages — never auto-sends messages.{' '}
            <span className="text-blue-300/60">{SALAR_PUBLIC_FACTS.emails.general}</span>
          </p>
        </div>
      )}
    </>
  );
};

export default VikkiAssistant;
